// src/utils/cacheUtils.js (or wherever you keep utilities)

const CACHE_PREFIX = "firestoreCache_";
const READ_COUNT_KEY = "firestoreReadCount";
const CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes

// Initialize read count from sessionStorage or start at 0
let sessionReadCount = parseInt(
  sessionStorage.getItem(READ_COUNT_KEY) || "0",
  10
);

/**
 * Generates a unique key for sessionStorage.
 * @param {string} docPath - The Firestore document path.
 * @param {string} [fieldName] - Optional field name within the document.
 * @returns {string} The cache key.
 */
const generateCacheKey = (docPath, fieldName) => {
  return `${CACHE_PREFIX}${docPath}${fieldName ? `/${fieldName}` : ""}`;
};

/**
 * Attempts to retrieve valid cached data.
 * @param {string} key - The cache key.
 * @returns {any | null} The cached data if valid, otherwise null.
 */
export const getCachedData = (key) => {
  try {
    const cachedItem = sessionStorage.getItem(key);
    if (!cachedItem) {
      return null;
    }

    const { timestamp, data } = JSON.parse(cachedItem);
    const now = Date.now();

    if (now - timestamp < CACHE_DURATION_MS) {
      console.log(`✅ [CACHE] Using cached data for key: ${key}`);
      return data; // Return data if cache is fresh
    } else {
      console.log(`⌛ [CACHE] Cache expired for key: ${key}`);
      sessionStorage.removeItem(key); // Remove expired item
      return null;
    }
  } catch (error) {
    console.error(`❌ [CACHE] Error reading cache for key ${key}:`, error);
    sessionStorage.removeItem(key); // Remove potentially corrupted item
    return null;
  }
};

/**
 * Stores data in the cache with a timestamp.
 * @param {string} key - The cache key.
 * @param {any} data - The data to cache.
 */
export const setCachedData = (key, data) => {
  try {
    const itemToCache = {
      timestamp: Date.now(),
      data: data,
    };
    sessionStorage.setItem(key, JSON.stringify(itemToCache));
    console.log(`💾 [CACHE] Stored data for key: ${key}`);
  } catch (error) {
    console.error(`❌ [CACHE] Error writing cache for key ${key}:`, error);
    // Handle potential storage limit errors if necessary
  }
};

/**
 * Removes an item from the cache.
 * @param {string} key - The cache key.
 */
export const invalidateCache = (key) => {
  sessionStorage.removeItem(key);
  console.log(`🗑️ [CACHE] Invalidated cache for key: ${key}`);
};

/**
 * Increments the session's Firestore read count.
 */
export const incrementReadCount = () => {
  sessionReadCount++;
  sessionStorage.setItem(READ_COUNT_KEY, sessionReadCount.toString());
  return sessionReadCount; // Return the new count
};

/**
 * Gets the current session's Firestore read count.
 */
export const getReadCount = () => {
  return sessionReadCount;
};
